<template lang="pug">
.c-help-insights(v-loading="loading")
  .c-help-insights__filters
    el-date-picker(
      v-model="range"
      type="daterange"
      range-separator="To"
      start-placeholder="Start date"
      end-placeholder="End date"
      value-format="YYYY-MM-DD"
      @change="load"
    )
    span.c-help-insights__hint Last 30 days when empty

  template(v-if="data")
    h3.c-help-insights__heading Tasks by tips opened
    .c-help-insights__card
      el-table(:data="data.tasks" style="width: 100%")
        el-table-column(label="Task" prop="title" min-width="200")
        el-table-column(label="Key" prop="taskKey" width="200")
        el-table-column(label="Tips opened" prop="tipOpens" width="130")
        el-table-column(label="Accounts" prop="tipOpenAccounts" width="120")
        el-table-column(label="Action clicks" prop="actionClicks" width="140")
        el-table-column(label="Manual done" prop="manualCompletions" width="140")

    h3.c-help-insights__heading Help links by opens
    .c-help-insights__card
      el-table(:data="data.links" style="width: 100%")
        el-table-column(label="Link" prop="title" min-width="160")
        el-table-column(label="URL" prop="url" min-width="240")
        el-table-column(label="Opens" prop="opens" width="100")
        el-table-column(label="Accounts" prop="accounts" width="110")

    h3.c-help-insights__heading Latest questions ({{ data.questionCount }})
    .c-help-insights__card
      el-table(:data="data.questions" style="width: 100%")
        el-table-column(label="Question" prop="question" min-width="280")
        el-table-column(label="Asked by" min-width="200")
          template(#default="{ row }")
            div {{ row.userName }}
            small {{ row.userEmail }}
        el-table-column(label="Organization" prop="entityName" min-width="160")
        el-table-column(label="Page" prop="page" min-width="160")
        el-table-column(label="When" width="200")
          template(#default="{ row }") {{ formatDate(row.createdAt) }}
    el-pagination.mt-3(
      v-if="data.questionCount > pageSize"
      layout="prev, pager, next"
      :page-size="pageSize"
      :total="data.questionCount"
      v-model:current-page="page"
      @current-change="load"
    )
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import dayjs from 'dayjs';
import globalShared from '@/shared';
import { findHelpInsights } from '@/graphs/gql';

export default defineComponent({
  name: 'HelpInsights',
  setup() {
    const { makeGraphRequest } = globalShared();
    const loading = ref(false);
    const data = ref<any>(null);
    const range = ref<string[] | null>(null);
    const page = ref(1);
    const pageSize = 20;

    async function load() {
      loading.value = true;
      try {
        const response = await makeGraphRequest('query', findHelpInsights, {
          from: range.value ? range.value[0] : null,
          to: range.value ? range.value[1] : null,
          skip: (page.value - 1) * pageSize,
          take: pageSize,
        });
        if (response.errors) throw new Error(response.errors[0].message);
        data.value = response.data.findHelpInsights;
      } catch (e: any) {
        ElMessage.error(e.message || 'Could not load insights');
      } finally {
        loading.value = false;
      }
    }

    const formatDate = (value: string) => dayjs(value).format('MMM D, YYYY h:mm A');
    onMounted(load);
    return {
      loading, data, range, page, pageSize, load, formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/views/manage-users-and-organizations/crm-list';

.c-help-insights {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__filters {
    display: flex;
    align-items: center;
    gap: 12px;

    :deep(.el-date-editor) {
      width: 320px;
    }
  }

  &__hint {
    font-size: 0.75rem;
    color: $gray-600;
  }

  &__heading {
    margin: 0.5rem 0 0;
    font-size: 1rem;
    font-weight: 600;
    color: $gray-900;
  }
}

@include crm-table('c-help-insights__card');

.c-help-insights__card :deep(.el-table__row) {
  cursor: default;
}
</style>
